import { useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import { MarketingShell } from '@/components/marketing-shell';
import { brandCopy, getBrand, setPageSeo } from '@/lib/brand';
import { CLAUDE_CONNECTOR_META } from './claude-connector-meta';

const CONNECTOR_URL = CLAUDE_CONNECTOR_META.connectorUrl;
const CLAUDE_CONNECTORS_URL = CLAUDE_CONNECTOR_META.claudeConnectorsUrl;
const API_KEYS_URL = CLAUDE_CONNECTOR_META.apiKeysUrl;
const API_KEY_PLACEHOLDER = 'okra_YOUR_API_KEY';
const CLAUDE_CODE_COMMAND = `claude mcp add --transport http okrapdf ${CONNECTOR_URL} \\
  --header "Authorization: Bearer ${API_KEY_PLACEHOLDER}"`;

function updateMeta() {
  setPageSeo({
    title: CLAUDE_CONNECTOR_META.title,
    description: CLAUDE_CONNECTOR_META.description,
    canonical: CLAUDE_CONNECTOR_META.canonical,
  });
}

export function ClaudeConnectorPage() {
  useEffect(() => {
    updateMeta();
  }, []);

  return (
    <MarketingShell>
      <main className="bg-background text-foreground">
        <section className="border-b border-border">
          <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
            <div className="max-w-3xl">
              <span className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1.5 font-mono text-[11px] font-medium text-primary shadow-okra-xs">
                MCP connector
              </span>
              <h1 className="mt-6 font-serif text-[clamp(36px,6vw,62px)] font-semibold leading-[1.04] tracking-[-0.025em]">
                {brandCopy(CLAUDE_CONNECTOR_META.h1, getBrand())}
              </h1>
              <p className="mt-5 max-w-[58ch] text-[17px] leading-relaxed text-muted-foreground">
                {brandCopy(CLAUDE_CONNECTOR_META.subhead, getBrand())}
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a
                  href={API_KEYS_URL}
                  className="inline-flex h-11 items-center gap-2 rounded-xl bg-primary px-5 text-[14px] font-semibold text-primary-foreground shadow-sm transition hover:-translate-y-px hover:opacity-90"
                >
                  Get an API key <ExternalLink className="h-4 w-4" />
                </a>
                <a
                  href={CLAUDE_CONNECTORS_URL}
                  className="inline-flex h-11 items-center gap-2 rounded-xl border border-border bg-card px-5 text-[14px] font-semibold text-foreground shadow-okra-xs transition hover:-translate-y-px hover:bg-muted"
                >
                  Open Claude Connectors <ExternalLink className="h-4 w-4" />
                </a>
                <a
                  href="#legacy-config"
                  className="inline-flex h-11 items-center rounded-xl border border-border bg-card px-5 text-[14px] font-semibold text-foreground shadow-okra-xs transition hover:-translate-y-px hover:bg-muted"
                >
                  Local config fallback
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-muted">
          <div className="mx-auto grid max-w-5xl gap-10 px-6 py-14 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <div className="text-[12px] font-semibold uppercase tracking-[0.04em] text-primary">
                Setup
              </div>
              <h2 className="mt-2 font-serif text-[30px] font-semibold tracking-[-0.02em]">
                API key required
              </h2>
              <p className="mt-3 text-[14px] leading-relaxed text-muted-foreground">
                {brandCopy(CLAUDE_CONNECTOR_META.apiKeyNote, getBrand())}
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5 shadow-okra-xs">
              <div className="mb-2 text-[12px] font-medium text-muted-foreground">Claude Code</div>
              <pre className="overflow-x-auto rounded-xl bg-foreground p-4 font-mono text-[13px] leading-relaxed text-background">
                <code>{CLAUDE_CODE_COMMAND}</code>
              </pre>
              <div className="mb-2 mt-5 text-[12px] font-medium text-muted-foreground">
                Remote MCP App URL
              </div>
              <pre className="overflow-x-auto rounded-xl bg-foreground p-4 font-mono text-[13px] leading-relaxed text-background">
                <code>{CONNECTOR_URL}</code>
              </pre>
              <p className="mt-4 text-[13px] leading-relaxed text-muted-foreground">
                {brandCopy(CLAUDE_CONNECTOR_META.desktopNote, getBrand())}
              </p>
            </div>
          </div>
        </section>

        <section id="legacy-config">
          <div className="mx-auto grid max-w-5xl gap-10 px-6 py-14 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <div className="text-[12px] font-semibold uppercase tracking-[0.04em] text-primary">
                Older clients
              </div>
              <h2 className="mt-2 font-serif text-[30px] font-semibold tracking-[-0.02em]">
                Stdio bridge
              </h2>
              <p className="mt-3 text-[14px] leading-relaxed text-muted-foreground">
                If your Claude Desktop build only supports local servers in
                <code className="mx-1 rounded bg-card px-1.5 py-0.5 font-mono text-[12px]">
                  claude_desktop_config.json
                </code>
                , bridge the hosted MCP endpoint with <code className="rounded bg-card px-1.5 py-0.5 font-mono text-[12px]">mcp-remote</code> and pass
                your API key as a header.
              </p>
            </div>
            <pre className="overflow-x-auto rounded-2xl border border-border bg-foreground p-5 font-mono text-[12.5px] leading-relaxed text-background shadow-okra-xs">
              <code>{`{
  "mcpServers": {
    "okrapdf": {
      "command": "npx",
      "args": [
        "-y",
        "mcp-remote",
        "${CONNECTOR_URL}",
        "--header",
        "Authorization: Bearer ${API_KEY_PLACEHOLDER}"
      ]
    }
  }
}`}</code>
            </pre>
          </div>
        </section>
      </main>
    </MarketingShell>
  );
}
